import { Head, usePage } from "@inertiajs/react"
import { useState } from "react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Pagination } from "@/components/pagination"
import { EmptyState } from "@/components/empty-state"
import { ViewModeToggle } from "@/components/projects/view-mode-toggle"
import { PipeStatusCombobox } from "@/components/projects/pipe-status-combobox"
import { AdvancedFiltersPopover } from "@/components/projects/advanced-filters-popover"
import { SortControls } from "@/components/projects/sort-controls"
import { ProjectNameCell } from "@/components/projects/cells/project-name-cell"
import { PropertyTypeCell } from "@/components/projects/cells/property-type-cell"
import { ContactCell } from "@/components/projects/cells/contact-cell"
import { PipeStatusCell } from "@/components/projects/cells/pipe-status-cell"
import { DatesCell } from "@/components/projects/cells/dates-cell"
import { ProjectFormDialog } from "@/components/projects/project-form-dialog"
import { MergeProjectsDialog } from "@/components/projects/merge-projects-dialog"
import { CourrierDialog } from "@/components/projects/courrier-dialog"
import { ProjectImportDialog } from "@/components/projects/project-import-dialog"
import { useProjectFilters } from "@/hooks/use-project-filters"
import { COLUMNS, STATUS_CONFIG } from "@/lib/project-configs"
import { Plus, Search, FolderOpen, GitMerge, Mail, Upload, Download } from "lucide-react"
import { downloadFile } from "@/lib/download"
import type { Project, PagyMetadata, SharedProps, ProjectTag, ProjectStatus, PipeStatusRecord, CourrierTemplate } from "@/types"

type Props = {
  projects: Project[]
  status: ProjectStatus
  base_url: string
  filters: Record<string, string>
  pagy: PagyMetadata
  organisations: { id: string; name: string }[]
  users: { id: string; first_name: string; last_name: string }[]
  teams: { id: string; name: string }[]
  project_tags: ProjectTag[]
  pipe_statuses: PipeStatusRecord[]
  courrier_templates: CourrierTemplate[]
  nav_key: string
}

function ProjectsIndex({ projects, status, base_url, filters, pagy, users, teams, project_tags, pipe_statuses, courrier_templates, nav_key }: Props) {
  const { permissions } = usePage<SharedProps>().props
  const canCreate = permissions?.projects.create
  const canMerge = permissions?.projects.merge
  const canImport = permissions?.projects.import
  const canCourrier = permissions?.courrier_templates?.index

  const [showProjectForm, setShowProjectForm] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showMergeDialog, setShowMergeDialog] = useState(false)
  const [showCourrierDialog, setShowCourrierDialog] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [exporting, setExporting] = useState(false)

  const allSelected = projects.length > 0 && projects.every((p) => selectedIds.has(p.id))
  const someSelected = projects.some((p) => selectedIds.has(p.id))

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(projects.map((p) => p.id)))
    }
  }

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectedProjects = projects.filter((p) => selectedIds.has(p.id))

  const handleExportSelected = async () => {
    setExporting(true)
    try {
      await downloadFile("/projects/export_csv", { project_ids: Array.from(selectedIds) }, "projets.csv")
    } catch {
      // silently fail
    } finally {
      setExporting(false)
    }
  }

  const handleExportAll = async () => {
    setExporting(true)
    try {
      await downloadFile("/projects/export_csv", { status, q: filters }, "projets.csv")
    } catch {
      // silently fail
    } finally {
      setExporting(false)
    }
  }

  const {
    search,
    setSearch,
    inputRef,
    activePipeStatus,
    setPipeStatusFilter,
    sortColumn,
    sortDirection,
    setSort,
    toggleSortDirection,
    advancedOpen,
    setAdvancedOpen,
    advancedFilters,
    activeAdvancedCount,
    setAdvanced,
    setAdvancedDebounced,
    resetAdvancedFilters,
  } = useProjectFilters({ filters, baseUrl: base_url })

  const pageTitle = STATUS_CONFIG[status]?.label || "Projets"

  const renderCell = (project: Project, key: string) => {
    switch (key) {
      case "name":
        return <ProjectNameCell key={key} project={project} navKey={nav_key} />
      case "property_type":
        return <PropertyTypeCell key={key} project={project} />
      case "contact":
        return <ContactCell key={key} project={project} />
      case "pipe_status":
        return (
          <PipeStatusCell
            key={key}
            project={project}
            availableTags={project_tags}
            pipeStatuses={pipe_statuses}
          />
        )
      case "dates":
        return <DatesCell key={key} project={project} />
      case "actions":
        return <TableCell key={key} className="w-10 text-right" />
    }
  }

  return (
    <>
      <Head title={pageTitle} />
      <PageHeader title={pageTitle} />
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ViewModeToggle baseUrl={base_url} />
            {canMerge && selectedIds.size === 2 && (
              <Button variant="outline" onClick={() => setShowMergeDialog(true)}>
                <GitMerge className="size-4" />
                Fusionner
              </Button>
            )}
            {canCourrier && selectedIds.size > 0 && (
              <Button variant="outline" onClick={() => setShowCourrierDialog(true)} disabled>
                <Mail className="size-4" />
                Courrier
              </Button>
            )}
            {selectedIds.size > 0 && (
              <Button variant="outline" onClick={handleExportSelected} disabled={exporting}>
                <Download className="size-4" />
                Exporter
              </Button>
            )}
            {selectedIds.size > 0 && (
              <span className="text-muted-foreground text-sm">
                {selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExportAll} disabled={exporting}>
              <Download className="size-4" />
              Exporter
            </Button>
            {canImport && (
              <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                <Upload className="size-4" />
                Importer
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => setShowProjectForm(true)}>
                <Plus className="size-4" />
                Nouveau projet
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {pipe_statuses.filter((ps) => ps.parent_status === status).length > 1 && (
            <PipeStatusCombobox value={activePipeStatus} onChange={setPipeStatusFilter} status={status} pipeStatuses={pipe_statuses} />
          )}
          <AdvancedFiltersPopover
            open={advancedOpen}
            onOpenChange={setAdvancedOpen}
            advancedFilters={advancedFilters}
            activeAdvancedCount={activeAdvancedCount}
            setAdvanced={setAdvanced}
            setAdvancedDebounced={setAdvancedDebounced}
            resetAdvancedFilters={resetAdvancedFilters}
            users={users}
            teams={teams}
            project_tags={project_tags}
          />
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={inputRef}
              placeholder="Rechercher par nom ou ville..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
            {pagy.count} résultat{pagy.count > 1 ? "s" : ""}
          </span>
          <SortControls
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={setSort}
            onToggleDirection={toggleSortDirection}
          />
        </div>

        <Card>
          <CardContent className="px-0">
            {projects.length === 0 ? (
              <EmptyState
                icon={FolderOpen}
                title="Aucun projet trouvé"
                description="Essayez de modifier vos critères de recherche ou vos filtres."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {(canMerge || canCourrier) && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allSelected ? true : someSelected ? "indeterminate" : false}
                          onCheckedChange={toggleAll}
                        />
                      </TableHead>
                    )}
                    {COLUMNS.map((col) => (
                      <TableHead key={col.key}>{col.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow key={project.id} data-state={selectedIds.has(project.id) ? "selected" : undefined}>
                      {(canMerge || canCourrier) && (
                        <TableCell className="w-10">
                          <Checkbox
                            checked={selectedIds.has(project.id)}
                            onCheckedChange={() => toggleSelection(project.id)}
                          />
                        </TableCell>
                      )}
                      {COLUMNS.map((col) => renderCell(project, col.key))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <CardFooter>
            <Pagination pagy={pagy} baseUrl={base_url} filters={filters} className="w-full p-0" />
          </CardFooter>
        </Card>
      </div>

      <ProjectFormDialog
        open={showProjectForm}
        onOpenChange={setShowProjectForm}
        users={users}
        teams={teams}
        project_tags={project_tags}
        pipeStatuses={pipe_statuses}
      />

      {canMerge && selectedProjects.length === 2 && (
        <MergeProjectsDialog
          open={showMergeDialog}
          onOpenChange={setShowMergeDialog}
          projectA={selectedProjects[0]}
          projectB={selectedProjects[1]}
          onSuccess={() => setSelectedIds(new Set())}
        />
      )}

      {canCourrier && (
        <CourrierDialog
          open={showCourrierDialog}
          onOpenChange={setShowCourrierDialog}
          selectedProjectIds={Array.from(selectedIds)}
          templates={courrier_templates || []}
        />
      )}

      {canImport && (
        <ProjectImportDialog
          open={showImportDialog}
          onOpenChange={setShowImportDialog}
          pipeStatuses={pipe_statuses}
          currentStatus={status}
        />
      )}
    </>
  )
}

ProjectsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default ProjectsIndex
